import React from "react";
import UsernameInput from "./ValidatedFields/UsernameInput";
import FirstNameInput from "./ValidatedFields/WithErrorLabel/FirstNameInput";
import { EmailInput } from ".";
import LastNameInput from "./ValidatedFields/WithErrorLabel/LastNameInput";
import { Button } from "semantic-ui-react";
import ValidatorContext from "../formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import PanelSegmentType from "./panels/PanelSegment/PanelSegment";
import DoublePasswordInput from "./ValidatedFields/WithErrorLabel/DoublePasswordInput";
import CulturesInterfaceDropdown from "./dropdowns/reactselect/CulturesInterfaceDropdown";

interface Props {
  signInUrl: string
  suggestedUserDetails: server.dto.SuggestedUserDetails
  suggestedLanguage: server.dto.LCID
  onSubmit: (email: string, firstName: string, lastName: string, password: string, username: string, language: server.dto.Culture) => Promise<void>
}

const getClosestInterfaceCulture = (lcid: server.dto.LCID) => {
  const defaultCulture = serverReference.interfaceCultures.find(c => c.locale === serverReference.constants.account.defaultInterfaceLocale);

  if (!lcid) {
    return defaultCulture;
  }

  const cultureAlreadyIsInterfaceCulture = serverReference.interfaceCultures.find(c => c.value === lcid);
  if (cultureAlreadyIsInterfaceCulture) {
    return cultureAlreadyIsInterfaceCulture;
  }

  const cultureFromLcid = serverReference.cultures.find(c => c.value === lcid) ?? defaultCulture;

  // Try to find the closest culture by matching the language. E.g. Dutch_Belgium can map to the Dutch_Netherlands interface culture
  const closestCulture = serverReference.interfaceCultures.find(c => c.value === cultureFromLcid.language);

  return closestCulture ?? defaultCulture;
};

const RegistrationForm = (props: Props) => {
  const [email, setEmail] = React.useState(props.suggestedUserDetails.email ?? "");
  const [username, setUsername] = React.useState("");
  const [password1, setPassword1] = React.useState("");
  const [password2, setPassword2] = React.useState("");
  const [firstName, setFirstName] = React.useState(props.suggestedUserDetails.name?.firstName ?? "");
  const [lastName, setLastName] = React.useState(props.suggestedUserDetails.name?.lastName ?? "");
  const [language, setLanguage] = React.useState(getClosestInterfaceCulture(props.suggestedLanguage));

  const [isLoading, setIsLoading] = React.useState(false);

  const validators = useValidators();

  const onSubmit = async (e) => {
    e?.preventDefault();

    setIsLoading(true);
    const validationResult = await validators.validateAll();
    if (validationResult.hasErrors) {
      setIsLoading(false);
      return;
    }

    props.onSubmit(email, firstName, lastName, password1, username, language);
  };

  return (
    <ValidatorContext settings={validators.validatorSettings}>
      <div>

        <PanelSegmentType>
          <PanelSegmentType.Header size="h2">
            <PanelSegmentType.Title title={RESX.Account.CreateAnAccount} />
          </PanelSegmentType.Header>
        </PanelSegmentType>

        <form action="" onSubmit={onSubmit}>
          <table className="s1_table bordergrid">
            <tbody>
              <tr>
                <td>{RESX.GeneralLabels.Name}:</td>
                <td>
                  <div className="flex">
                    <div className="grow1 margin-right">
                      <FirstNameInput
                        value={firstName}
                        onChange={setFirstName}
                        validatorKey="firstName"
                      />
                    </div>
                    <div className="grow1">
                      <LastNameInput
                        value={lastName}
                        onChange={setLastName}
                        validatorKey="lastName"
                      />
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td>{RESX.GeneralLabels.Email}:</td>
                <td>
                  <EmailInput
                    value={email}
                    onChange={setEmail}
                    validatorKey="email"
                    emailCanBeBlank={false}
                  />
                </td>
              </tr>
              <tr>
                <td>{RESX.SoUser.Username}:</td>
                <td>
                  <UsernameInput
                    value={username}
                    onChange={setUsername}
                    validatorKey="username"
                  />
                </td>
              </tr>
              <tr>
                <td>{RESX.PersonalSettings.Password}:</td>
                <td>
                  <DoublePasswordInput.FirstPasswordInput
                    value={password1}
                    validatorKey="password"
                    onChange={setPassword1}
                  />
                </td>
              </tr>
              <tr>
                <td>{RESX.PersonalSettings.RetypePassword}:</td>
                <td>
                  <DoublePasswordInput.SecondPasswordInput
                    value={password2}
                    firstPassword={password1}
                    validatorKey="confirmPassword"
                    onChange={setPassword2}
                  />
                </td>
              </tr>
              <tr>
                <td>{RESX.Language.resxLanguage}</td>
                <td>
                  <CulturesInterfaceDropdown
                    value={language}
                    onChange={setLanguage}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="flex align-end">
            <div className="grow1">
              <a href={props.signInUrl}>{RESX.Account.AlreadyHaveAnAccount}</a>
            </div>
            <div>
              <Button
                primary
                onClick={onSubmit}
                loading={isLoading}
              >{RESX.Account.btnCreateAccount}</Button>
            </div>
          </div>
        </form>
      </div>
    </ValidatorContext >
  );
};

export default RegistrationForm;
